import { useEffect, useRef, useState } from 'react';
import { Dialog } from './ui';
import { haptic } from '../lib/haptics';

interface DetectedBarcode {
  rawValue: string;
}
interface BarcodeDetectorLike {
  detect(source: HTMLVideoElement): Promise<DetectedBarcode[]>;
}
declare global {
  interface Window {
    BarcodeDetector?: {
      new (opts?: { formats?: string[] }): BarcodeDetectorLike;
      getSupportedFormats?: () => Promise<string[]>;
    };
  }
}

const FORMATS = ['ean_13', 'ean_8', 'upc_a', 'upc_e', 'code_128', 'code_39', 'itf', 'qr_code'];

/**
 * Camera barcode/QR scanner. Uses the browser's BarcodeDetector where available (Chrome on Android,
 * desktop Chrome/Edge) and falls back to ZXing (iOS Safari, Firefox). USB/Bluetooth scanners don't
 * need this: they type into the focused search box and press Enter.
 */
export function CameraScanner({ open, onClose, onResult }: { open: boolean; onClose: () => void; onResult: (code: string) => void }) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    let stopped = false;
    let stream: MediaStream | null = null;
    let stopZxing: (() => void) | null = null;
    setError(null);

    const finish = (code: string) => {
      if (stopped) return;
      stopped = true;
      haptic([20, 40, 20]);
      onResult(code.trim());
    };

    (async () => {
      if (!window.isSecureContext) {
        setError('The camera only works when the dashboard is opened over a secure (https://) link. Type or scan the code into the search box instead.');
        return;
      }
      if (!navigator.mediaDevices?.getUserMedia) {
        setError('This browser cannot use the camera. Type or scan the code into the search box instead.');
        return;
      }
      try {
        stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: { ideal: 'environment' } }, audio: false });
      } catch {
        setError('Camera access was blocked. Allow camera access for this site, or type the code instead.');
        return;
      }
      const video = videoRef.current;
      if (!video || stopped) return;
      video.srcObject = stream;
      await video.play().catch(() => undefined);

      if (window.BarcodeDetector) {
        const supported = (await window.BarcodeDetector.getSupportedFormats?.()) ?? FORMATS;
        const detector = new window.BarcodeDetector({ formats: FORMATS.filter((f) => supported.includes(f)) });
        const tick = async () => {
          if (stopped) return;
          try {
            const [hit] = await detector.detect(video);
            if (hit?.rawValue) return finish(hit.rawValue);
          } catch {
            /* frame not ready */
          }
          requestAnimationFrame(() => void tick());
        };
        void tick();
      } else {
        const { BrowserMultiFormatReader } = await import('@zxing/browser');
        const reader = new BrowserMultiFormatReader();
        const controls = await reader.decodeFromVideoElement(video, (result) => {
          if (result) finish(result.getText());
        });
        stopZxing = () => controls.stop();
      }
    })();

    return () => {
      stopped = true;
      stopZxing?.();
      stream?.getTracks().forEach((t) => t.stop());
    };
  }, [open, onResult]);

  return (
    <Dialog open={open} onClose={onClose} title="Scan a code">
      {error ? (
        <p className="alert alert--danger">{error}</p>
      ) : (
        <div className="scanner">
          <video ref={videoRef} className="scanner__video" muted playsInline />
          <div className="scanner__frame" aria-hidden="true" />
          <p className="muted small">Point the camera at the barcode or QR code.</p>
        </div>
      )}
    </Dialog>
  );
}
